import {startupBufferDecision, hlsErrorAction} from 'utils/ultraPlayerEngine';

/**
 * Player D freeze-profile decisions.
 *
 * DPlayer.js is a fork of UltraPlayer.js that replaces Player C's
 * "slate + bounded stall budget" recovery with "freeze the last frame with
 * infinite patience while the relay says the session is alive". The pure
 * decisions that diverge from C live here; everything shared (backoff,
 * progress, buffer measurements, resume-at-sequence) stays imported from
 * ultraPlayerEngine.js untouched.
 */

/** A relay status sample older than this no longer proves the session lives. */
export const D_STATUS_FRESH_MS = 10000;
/** With media AND status both silent, freeze this long past staleness, then restart. */
export const D_STATUS_LOST_STALL_BUDGET_MS = 15000;
/** The discreet "Reconectando…" chip appears after this much frozen time. */
export const FREEZE_CHIP_AFTER_MS = 5000;
/** Throttle for in-place playlist reloads after fatal manifest/level errors. */
export const D_PLAYLIST_RELOAD_MIN_INTERVAL_MS = 3000;
/** Screen manager refresh cadence while waiting for a free screen. */
export const SCREENS_POLL_MS = 5000;
/**
 * `ready` is included: there is a window where upstream already stalled but
 * the relay still reports ready while the hidden reserve drains — the client
 * must already be freezing there, never restarting.
 */
export const D_RELAY_ALIVE_STATES = Object.freeze(['waiting', 'starting', 'recovering', 'ready']);

const PLAYLIST_TERMINAL_STATUSES = new Set([401, 403, 404]);
const MANIFEST_OR_LEVEL_DETAILS = /^(manifest|level)/i;

/**
 * Liveness of the relay session as seen through status.json samples.
 * `stopped`: the relay ended the session (fresh sample says so).
 * `lost`: no valid sample within the freshness window.
 */
export function relayAliveness(sample, nowMs = Date.now()) {
  const receivedAtMs = Number(sample?.receivedAtMs);
  if (!sample || !Number.isFinite(receivedAtMs)) {
    return {status: 'lost', state: null, ageMs: Infinity};
  }
  const ageMs = Math.max(0, nowMs - receivedAtMs);
  if (ageMs > D_STATUS_FRESH_MS) return {status: 'lost', state: sample.state || null, ageMs};
  if (sample.state === 'stopped') return {status: 'stopped', state: 'stopped', ageMs};
  if (D_RELAY_ALIVE_STATES.includes(sample.state)) {
    return {status: 'alive', state: sample.state, ageMs};
  }
  return {status: 'lost', state: sample.state || null, ageMs};
}

/**
 * The stall clause of the D watchdog. Never restarts while the relay is
 * alive — that is the whole point of the freeze profile. A fresh `stopped`
 * restarts immediately (re-bootstrap mints a session); a lost status gets a
 * bounded budget counted from the moment the sample went stale, because with
 * media AND status both dark, channel-dead and network-path-dead look alike
 * and a resume-at-sequence restart is cheap and correct.
 */
export function dStallDecision({aliveness, frozenForMs = 0}) {
  const showChip = frozenForMs >= FREEZE_CHIP_AFTER_MS;
  if (!aliveness || aliveness.status === 'alive') {
    return {action: 'hold', reason: 'upstreamOutage', showChip};
  }
  if (aliveness.status === 'stopped') {
    return {action: 'restart', reason: 'sessionStopped', showChip: false};
  }
  const lostForMs = Number.isFinite(aliveness.ageMs)
    ? Math.max(0, aliveness.ageMs - D_STATUS_FRESH_MS)
    : frozenForMs;
  if (lostForMs >= D_STATUS_LOST_STALL_BUDGET_MS) {
    return {action: 'restart', reason: 'statusLost', showChip: false};
  }
  return {action: 'hold', reason: 'statusLost', showChip};
}

/**
 * Startup wrapper: the shared thresholds (ready >= 10 s, degraded >= 2 s at
 * 12 s) are untouched, but the 20-second empty-buffer restart is suspended
 * while the relay is legitimately still producing the first cushion
 * (waiting/starting/recovering). A relay that says `ready` with a buffer
 * that stays empty points at a wedged local pipeline: restart normally.
 */
export function dStartupDecision(bufferedSeconds, elapsedMs, aliveness) {
  const decision = startupBufferDecision(bufferedSeconds, elapsedMs);
  if (
    decision === 'restart' &&
    aliveness?.status === 'alive' &&
    ['waiting', 'starting', 'recovering'].includes(aliveness.state)
  ) {
    return 'wait';
  }
  return decision;
}

/**
 * hls.js ERROR decisions for the freeze profile. Wraps Player C's
 * hlsErrorAction with three D-specific rules:
 *  - there is no mpegts 'compatible' path (D is HLS-only by contract);
 *  - a playlist/level HTTP 401/403/404 is never terminal: the playlist URL is
 *    an ephemeral relay resource, so the session is re-bootstrapped and only
 *    the /play bootstrap may declare a channel terminal;
 *  - while the relay is alive, a fatal network manifest/level failure (the
 *    stale-playlist retries ran out) reloads in place with `startLoad(-1)` —
 *    throttled, uncapped — instead of burning restart attempts;
 *  - a non-fatal buffer stall while alive is `holdFrozen`: the freeze
 *    machinery owns presentation, not the BUFFERING overlay.
 */
export function dHlsErrorAction(
  data,
  {
    manifestParsed = false,
    mediaRecoveryAttempts = 0,
    inplaceReloadAttempts = 0,
    aliveness = null,
    lastPlaylistReloadAtMs = 0,
    nowMs = Date.now()
  } = {}
) {
  const base = hlsErrorAction(
    data,
    {manifestParsed, mediaRecoveryAttempts, inplaceReloadAttempts},
    () => false
  );
  const details = String(data?.details || '');
  const alive = aliveness?.status === 'alive';

  if (
    MANIFEST_OR_LEVEL_DETAILS.test(details) &&
    PLAYLIST_TERMINAL_STATUSES.has(base.classification.statusCode)
  ) {
    return {
      action: 'restartSession',
      classification: {...base.classification, retryable: true, reason: 'sessionStopped'}
    };
  }
  if (
    alive &&
    data?.fatal &&
    data?.type === 'networkError' &&
    MANIFEST_OR_LEVEL_DETAILS.test(details)
  ) {
    if (nowMs - lastPlaylistReloadAtMs >= D_PLAYLIST_RELOAD_MIN_INTERVAL_MS) {
      return {action: 'reloadPlaylist', classification: base.classification};
    }
    return {action: 'ignoreThrottled', classification: base.classification};
  }
  if (base.action === 'buffering' && alive) {
    return {action: 'holdFrozen', classification: base.classification};
  }
  return base;
}

/**
 * Safety net for the resume after a splice gap: when the playhead is stuck
 * outside every buffered range and new media exists wholly ahead beyond
 * hls.js's own maxBufferHole nudging (0.5 s), seek once just inside the new
 * range. Returns null whenever hls.js can resume by itself.
 */
export function resumeSeekTarget(buffered, currentTime) {
  if (!buffered || typeof buffered.length !== 'number') return null;
  const current = Number(currentTime);
  if (!Number.isFinite(current)) return null;
  let coveredWithDataAhead = false;
  let nextStart = null;
  try {
    for (let index = 0; index < buffered.length; index += 1) {
      const start = Number(buffered.start(index));
      const end = Number(buffered.end(index));
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
      if (current >= start && current < end - 0.05) coveredWithDataAhead = true;
      if (start > current && end - start > 0.1 && (nextStart === null || start < nextStart)) {
        nextStart = start;
      }
    }
  } catch (_) {
    return null;
  }
  if (coveredWithDataAhead || nextStart === null) return null;
  if (nextStart - current <= 0.5) return null;
  return nextStart + 0.1;
}

function countOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** "2 de 3 pantallas en uso" with graceful degradation for missing data. */
export function screenUsageLabel(activeCount, maxActiveStreams) {
  const used = countOrNull(activeCount);
  const max = countOrNull(maxActiveStreams);
  if (used === null && max === null) return 'Sin pantallas libres';
  if (used === null) return `${max} pantallas permitidas`;
  if (max === null) return `${used} pantallas en uso`;
  return `${used} de ${max} pantallas en uso`;
}
